// ═══════════════════════════════════════════════════════════════════════════
//  PEMUTAR — /p/KODE
//
//  Layar yang tampil di TV room. Bukan remote: tidak ada pencarian, tidak ada
//  daftar, tidak ada tombol kecil. Yang ada cuma lagu, lirik, dan skor.
//
//  Kodenya sama dengan remote — perangkatnya memang ada di room yang sama.
//
//  ── KENAPA PEMUTAR YANG MENGHITUNG SKOR, BUKAN SERVER ─────────────────────
//
//  Bahan bakunya aliran nada dari mikrofon, ~25 nilai per detik selama 4 menit.
//  Mengirimnya ke server berarti mengalirkan audio sepanjang lagu lewat wifi
//  outlet — dan saat wifi-nya tersendat, yang hilang bukan sekadar mutu, tapi
//  potongan penilaian yang membuat skornya salah tanpa ada yang tahu.
// ═══════════════════════════════════════════════════════════════════════════

import { usePerbaruiOtomatis } from '../lib/perbaruiTV'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Mic, Loader2, Music4, AlertTriangle, Clock } from 'lucide-react'
import {
  ambilRoom, ambilBerkas, tandai, kirimSkor, jam, nadaTampil, pesanGalat,
  tidakBerubah, laporPemutar, laporPosisi, uriSpotify,
  type KeadaanRoom, type BarisAntrean, type BerkasLagu,
  cekPerangkat,
} from '../lib/tamu'
import { DengarMik, Penilai } from '../lib/audio'
import { MesinStem, type Kemampuan, type ModeAudio } from '../lib/mesinStem'
import { pasangElAudio, useElAudio, aturElAudio, mintaMulaiElAudio } from '../lib/elAudio'
import {
  susunDariBerkas, mulaiLewatiIntro, ambilLirikLuar, ambilLirikLuarRinci, bacaSetelanLirik, romanisasiLirik, simpanSetelanLirik, buatJamHalus,
  type BarisKaraoke, type BerkasBerkata, type SetelanLirik,
} from '../lib/lirik'
import PasangLogin from './PasangLogin'
import {
  pasangSimpanan, putaranSimpan, langgananSimpan, ukuranTeks, type KeadaanSimpan,
} from '../lib/simpananLokal'
// 5 Okt 2026: lirik karaoke (sapuan biru) + hiasan layar TV.
import LirikKaraoke from './pemutar/LirikKaraoke'
import { LatarHidup, KartuMasuk, BerikutnyaPojok, Equalizer, Konfeti, AngkaNaik } from './pemutar/Hiasan'
import './pemutar/pemutar.css'

// ═══════════════ DARI MANA BERKAS LAGUNYA DIAMBIL ═══════════════════════════
//
// ★ INI BUKAN SEKADAR SETELAN — INI KEPUTUSAN RANCANGAN YANG SUDAH DIAMBIL ★
//
// nginx/karaoke.conf menyebutkannya dengan tegas: berkas media TIDAK PERNAH
// lewat VM pusat. 20.000 lagu ≈ 800 GB, dan sepuluh room menyanyi bersamaan
// ≈ 50 Mbps terus-menerus. Lagu disajikan server DI OUTLET, lewat LAN.
//
// Akibatnya alamat medianya BERBEDA di tiap outlet, sedangkan aplikasinya
// satu build untuk semua. Jadi VITE_MEDIA_URL — yang dibekukan saat build —
// tidak bisa menjadi jawabannya.
//
// Yang benar: disetel PER PERANGKAT. Satu TV box berada di satu outlet secara
// fisik, selamanya, jadi ia disetel sekali dan diingat.
//
// Kalau ini dibiarkan bawaan '/media', permintaannya jatuh ke `location /` di
// nginx dan dijawab index.html. Videonya tidak jalan, dan yang muncul di
// console adalah galat format media — tanpa satu pun petunjuk bahwa yang
// salah adalah alamat servernya.
const KUNCI_MEDIA = 'karaoke.media'

export const bacaMedia = (): string =>
  localStorage.getItem(KUNCI_MEDIA)
  || (import.meta.env.VITE_MEDIA_URL as string | undefined)
  || ''

export const simpanMedia = (v: string) =>
  localStorage.setItem(KUNCI_MEDIA, v.trim().replace(/\/+$/, ''))

// ── "BELUM ADA SERVERNYA" — PENANDA TERSENDIRI, BUKAN ALAMAT KOSONG ────────
//
// Alamat kosong TIDAK bisa dipakai sebagai penanda "sudah dilewati": penjaga
// di bawah berbunyi `if (!bacaMedia())`, jadi menyimpan '' membuat layar ini
// muncul lagi persis seperti sebelumnya — tombolnya seolah tidak berfungsi.
//
// Dua keadaan yang berbeda butuh dua penanda: "belum pernah diisi" dan "sudah
// diputuskan bahwa servernya memang belum ada". Menyatukannya di satu nilai
// kosong menghapus perbedaan itu.
const KUNCI_LEWATI = 'karaoke.media.lewati'
export const mediaDilewati = () => localStorage.getItem(KUNCI_LEWATI) === '1'
export const lewatiMedia = () => localStorage.setItem(KUNCI_LEWATI, '1')

const alamat = (p: string) => {
  const base = bacaMedia()
  if (/^https?:\/\//.test(p)) {
    // 21 Okt 2026: berkas tinggal di PC karaoke, server memberi URL absolut
    // (http://192.168.1.80:8090/…). Perangkat yang TIDAK satu LAN dengan PC
    // (mis. tes dari rumah lewat Tailscale) mengisi "Alamat server lagu" di
    // perangkat ini — bagian host-nya diganti, nama berkasnya tetap.
    // 26 Okt: HANYA alamat penyaji kita (nginx :8090) yang hostnya diganti.
    // Perpustakaan lama outlet (http://192.168.1.200:8080/Songs$1/…) diputar
    // apa adanya — berkasnya memang hanya ada di server itu.
    if (base) {
      try {
        const u = new URL(p)
        if (u.port !== '8090') return p
        return base + u.pathname
      } catch { return p }
    }
    return p
  }
  const bersih = p.replace(/^\/+/, '')

  // ── KALAU ALAMAT SERVER OUTLET BELUM ADA, PAKAI VM ────────────────────────
  //
  // Ini jalur DEMO, sepasang dengan GET /api/media/:nama di backend.
  //
  // Tanpa cadangan ini, `base` kosong membuat alamatnya menjadi "/nama.mp3" —
  // permintaan itu jatuh ke `location /` di nginx dan dijawab index.html.
  // Peramban menerima HTML sebagai berkas media, dan yang muncul adalah galat
  // format media: pesan yang tidak menyebut alamat server sama sekali, dan
  // membuat orang mengira berkas lagunya yang rusak.
  //
  // Begitu alamat server outlet diisi, baris ini tidak tersentuh lagi — dan
  // itu memang yang benar di produksi: berkas media tidak lewat VM pusat.
  if (!base) return `/api/media/${encodeURIComponent(bersih)}`

  return `${base}/${bersih}`
}

// ── TV KAMAR: /p/perangkat/<token> memasangkan TV ini ke satu room ─────────
//
// Sama dengan tablet remote (29 Sep 2026): TV disandingkan sekali oleh
// kapten, token disimpan di TV, dan layar pemutar terbuka sendiri tiap
// room-nya punya sesi — tanpa mengetik kode di TV. /p/KODE tetap berlaku.
const KUNCI_TV = 'kr_perangkat_tv'

// 3 Nov 2026 (Zaldy: "kursor di room karaoke masih kelihatan, bisa di-hide?"):
// kursor disembunyikan setelah 3 dtk diam dan muncul lagi begitu mouse
// digerakkan/diklik — staf tetap bisa memasangkan TV & mengisi alamat server.
function useSembunyiKursor(jeda = 3000) {
  useEffect(() => {
    const el = document.documentElement
    let t = 0
    const sembunyi = () => el.classList.add('tv-tanpa-kursor')
    const gerak = () => {
      el.classList.remove('tv-tanpa-kursor')
      window.clearTimeout(t)
      t = window.setTimeout(sembunyi, jeda)
    }
    gerak()
    const ev = ['mousemove', 'mousedown', 'keydown', 'touchstart'] as const
    ev.forEach(n => window.addEventListener(n, gerak, { passive: true }))
    return () => {
      ev.forEach(n => window.removeEventListener(n, gerak))
      window.clearTimeout(t)
      el.classList.remove('tv-tanpa-kursor')
    }
  }, [jeda])
}

export default function Pemutar() {
  useSembunyiKursor()
  // 21 Okt 2026: ?alamat=http://host:8090 → simpan alamat server lagu untuk
  // perangkat ini; ?alamat (kosong) → hapus, layar setel alamat muncul lagi.
  {
    const q = new URLSearchParams(location.search)
    if (q.has('alamat')) {
      const v = (q.get('alamat') || '').trim()
      try {
        if (v) simpanMedia(v)
        else { localStorage.removeItem(KUNCI_MEDIA); localStorage.removeItem(KUNCI_LEWATI) }
      } catch { /* privat */ }
      q.delete('alamat')
      history.replaceState(null, '', location.pathname + (q.toString() ? '?' + q : ''))
    }
  }
  const bagian = location.pathname.split('/')
  if (bagian[2] === 'perangkat' && bagian[3]) {
    try { localStorage.setItem(KUNCI_TV, bagian[3]) } catch { /* privat */ }
    history.replaceState(null, '', '/p')
  }
  let token = ''
  try { token = localStorage.getItem(KUNCI_TV) ?? '' } catch { /* privat */ }
  const kodePath = (bagian[2] || '').toUpperCase()
  if (token && !kodePath) return <PemutarPerangkat token={token} />
  // 2 Okt: TV belum dipasang → login staf + pilih room, bukan "kurang kode".
  if (!kodePath || kodePath === 'PASANG') {
    return <PasangLogin jenis="tv" kunci={KUNCI_TV} sesudah={() => location.replace('/p')} />
  }
  return <PemutarKode kode={kodePath} />
}

// ── SIMPANAN LOKAL (6 Okt 2026) ─────────────────────────────────────────────
// Hanya bila berkas lagu diambil dari server pusat (alamat server lagu outlet
// belum diisi). Unduh di jam sepi, ditunda selama ada video/audio diputar.
function PenjagaSimpanan({ token }: { token: string }) {
  const [k, setK] = useState<KeadaanSimpan | null>(null)
  useEffect(() => {
    if (bacaMedia()) return // outlet punya server lagu LAN — tidak perlu
    let hidup = true
    const lepas = langgananSimpan(x => { if (hidup) setK(x) })
    const sibuk = () => Array.from(document.querySelectorAll('video,audio'))
      .some(e => !(e as HTMLMediaElement).paused)
    const putar = () => { void putaranSimpan(token, { sibuk }) }
    void pasangSimpanan().then(ok => { if (ok && hidup) putar() })
    const iv = window.setInterval(putar, 30 * 60_000)
    return () => { hidup = false; lepas(); window.clearInterval(iv) }
  }, [token])
  if (!k?.didukung || (k.jumlah === 0 && !k.unduh)) return null
  return (
    <div className="fixed left-3 bottom-3 z-[60] text-[11px] text-white/55 bg-black/40 rounded-full px-3 py-1 pointer-events-none">
      💾 {k.jumlah} berkas lagu tersimpan · {ukuranTeks(k.byte)}
      {k.unduh && ` · mengunduh ${k.unduh.selesai}/${k.unduh.total}`}
    </div>
  )
}

function PemutarPerangkat({ token }: { token: string }) {
  const [jawab, setJawab] = useState<{ room: string; outlet: string; kode: string } | null>(null)
  const [galat, setGalat] = useState('')
  useEffect(() => {
    let hidup = true
    let t: number | undefined
    const tanya = async () => {
      try { const j = await cekPerangkat(token); if (hidup) { setJawab(j); setGalat('') } }
      catch (e) { if (hidup) setGalat(pesanGalat(e)) }
      t = window.setTimeout(tanya, jawab?.kode ? 20000 : 5000)
    }
    tanya()
    return () => { hidup = false; window.clearTimeout(t) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])
  // 27 Okt: versi baru dimuat sendiri selama room belum punya sesi.
  usePerbaruiOtomatis(!jawab?.kode)
  // 6 Okt 2026: simpanan lokal lagu — jalan terus di PC/TV room yang sudah
  // dipasangkan, baik saat room kosong (pagi, jam sepi) maupun saat dipakai.
  if (jawab?.kode) return <><PenjagaSimpanan token={token} /><PemutarKode key={jawab.kode} kode={jawab.kode} /></>
  return (
    <Layar>
      <PenjagaSimpanan token={token} />
      <div className="text-[13px] tracking-[0.3em] uppercase text-gold">{jawab ? `${jawab.outlet} · ${jawab.room}` : 'TV kamar'}</div>
      <h1 className="mt-4 text-4xl font-semibold">{galat ? 'TV belum terhubung' : 'Menunggu sesi dibuka'}</h1>
      <p className="mt-3 text-text-3 max-w-xl">
        {galat || 'Begitu booking room ini dimulai di ElPOS, layar karaoke terbuka sendiri.'}
      </p>
      {!galat && <Loader2 size={28} className="animate-spin text-text-3 mt-8" />}
      {galat && (
        <button onClick={() => { try { localStorage.removeItem(KUNCI_TV) } catch { /* privat */ } location.replace('/p') }}
          className="mt-8 px-5 py-3 rounded-2xl bg-gold text-black font-semibold">
          Pasang ulang (login staf)
        </button>
      )}
    </Layar>
  )
}

function PemutarKode({ kode }: { kode: string }) {
  const [room, setRoom] = useState<KeadaanRoom | null>(null)
  const [galat, setGalat] = useState('')
  const [sisa, setSisa] = useState<number | null>(null)
  const [, setUlang] = useState(0)

  // Sidik keadaan terakhir. Sama seperti di remote: kalau tidak ada yang
  // berubah, server menjawab {tetap:true} tanpa menjalankan kueri besarnya,
  // dan `room` sengaja TIDAK diset ulang. Objek baru yang isinya sama tetap
  // menggambar ulang seluruh layar pemutar — dan layar ini sedang memutar
  // video sambil menggambar lirik.
  const sidikRef = useRef('')
  const jalanRef = useRef<Promise<void> | null>(null)

  // `paksa` = permintaan ini lahir dari sebuah tindakan (lagu selesai, lagu
  // dilewati), bukan dari pengatur waktu. Yang begitu WAJIB jalan: kalau ia
  // ikut ditolak penjaga tumpang-tindih, lagu berikutnya baru mulai lima detik
  // kemudian, dan yang terlihat di TV adalah layar diam tanpa sebab.
  const muat = useCallback(async (paksa = false) => {
    if (jalanRef.current) {
      if (!paksa) return
      await jalanRef.current.catch(() => {})
    }
    const kerja = (async () => {
    try {
      const j = await ambilRoom(kode, paksa ? '' : sidikRef.current)
      sidikRef.current = j.sidik ?? ''
      // Sisa waktu disimpan TERPISAH dari `room`.
      //
      // Kalau ia ikut menumpang di `room`, angkanya membeku selama antrean
      // tidak berubah — dan antrean tidak berubah selama satu lagu berjalan,
      // 3–4 menit. Jam yang berhenti di layar TV terbaca sebagai sistem yang
      // hang, bukan sebagai penghematan.
      setSisa(tidakBerubah(j) ? j.sisa_detik : (j.room?.sisa_detik ?? null))
      if (!tidakBerubah(j)) setRoom(j)
      setGalat('')
    } catch (e) {
      sidikRef.current = ''
      setGalat(pesanGalat(e))
    }
    })()
    jalanRef.current = kerja
    try { await kerja } finally { jalanRef.current = null }
  }, [kode])

  const segarkan = useCallback(() => { void muat(true) }, [muat])

  // 3 Okt 2026: lagu yang HABIS di layar ini (bukan dibuka manual) → lagu
  // berikutnya dimulai sendiri. Penanda dipakai sekali oleh layar tunggu.
  const [otomatis, setOtomatis] = useState(false)
  const laguHabis = useCallback(() => { setOtomatis(true); void muat(true) }, [muat])
  const pakaiOtomatis = useCallback(() => setOtomatis(false), [])

  useEffect(() => {
    let t: number | undefined
    // setTimeout berantai: jeda dihitung sesudah jawaban datang, jadi
    // permintaan tidak pernah menumpuk saat jaringan outlet melambat.
    // 2,5 dtk (dulu 5): perintah vokal/musik/nada/jeda dari remote datang
    // lewat antrean ini — 5 detik terasa seperti tombol yang tidak bekerja.
    // Kuerinya murah selama sidik tidak berubah (lihat TamuRoom).
    const putar = () => { t = window.setTimeout(async () => { await muat(); putar() }, 2500) }
    muat(true)
    putar()
    return () => window.clearTimeout(t)
  }, [muat])

  // Hitung mundur berjalan di layar sendiri, disetel ulang tiap jawaban server.
  useEffect(() => {
    const t = setInterval(() => setSisa(s => (s === null || s <= 0 ? s : s - 1)), 1000)
    return () => clearInterval(t)
  }, [])

  // 5 Okt 2026: sapa ekstensi EL Audio (PC room) sejak layar dibuka, supaya
  // statusnya sudah diketahui saat lagu YouTube pertama mulai. Tanpa
  // ekstensi (APK, browser lain) tidak terjadi apa-apa.
  useEffect(() => { pasangElAudio() }, [])

  // 27 Okt: deploy baru termuat sendiri saat antrean room kosong (tanpa F5,
  // tanpa memutus lagu tamu).
  usePerbaruiOtomatis(!!room && !room.antrean.some(a => a.status === 'diputar' || a.status === 'menunggu'))

  if (!kode) return <Layar><p>Alamatnya kurang kode room: /p/KODE</p></Layar>
  // Ditanyakan SEBELUM lagu pertama, bukan saat lagu pertama gagal diputar.
  // Kegagalan memutar video terbaca sebagai berkas rusak, bukan sebagai
  // alamat server yang belum diisi.
  // Layar setel alamat hanya muncul kalau BELUM diisi DAN belum diputuskan
  // dilewati. Tanpa syarat kedua, tombol "belum ada servernya" mengembalikan
  // orang ke layar yang sama dan terlihat rusak.
  if (!bacaMedia() && !mediaDilewati()) {
    return <SetelMedia selesai={() => setUlang(x => x + 1)} />
  }
  if (galat) return <Layar><p className="text-red-400">{galat}</p></Layar>
  if (!room) return <Layar><Loader2 className="animate-spin" /></Layar>

  const sedang = room.antrean.find(a => a.status === 'diputar')
  const berikut = room.antrean.filter(a => a.status === 'menunggu')

  if (!sedang) {
    return <Diam kode={kode} room={room} berikut={berikut} sisa={sisa} sesudah={segarkan}
      otomatis={otomatis} pakaiOtomatis={pakaiOtomatis} />
  }
  // 3 Okt 2026: item luar — YouTube (IFrame resmi) / Spotify (aplikasi lokal).
  if (sedang.sumber === 'youtube') return <MainYouTube key={sedang.id} kode={kode} baris={sedang} berikut={berikut} sesudah={laguHabis} />
  if (sedang.sumber === 'spotify') return <MainSpotify key={sedang.id} kode={kode} baris={sedang} berikut={berikut} sesudah={laguHabis} />
  return <Main key={sedang.id} kode={kode} baris={sedang} berikut={berikut} sesudah={laguHabis} />
}

const Layar = ({ children }: { children: ReactNode }) => (
  <div className="min-h-screen relative overflow-hidden grid place-items-center text-center px-8 text-[15px]">
    <LatarHidup redup />
    <div className="relative z-[1]">{children}</div>
  </div>
)

// ── LAYAR TUNGGU ───────────────────────────────────────────────────────────

function Diam({ kode, room, berikut, sisa, sesudah, otomatis, pakaiOtomatis }: {
  // `sisa` datang sebagai prop, TIDAK ikut di dalam `room`.
  //
  // Sisa waktu berubah tiap detik; isi `room` hanya berubah saat antrean
  // berubah. Menyatukannya berarti salah satu dari dua hal harus mengalah:
  // entah `room` diganti tiap detik (seluruh layar digambar ulang sambil
  // memutar video), atau jamnya membeku 3–4 menit selama satu lagu berjalan.
  kode: string; room: KeadaanRoom; berikut: BarisAntrean[]
  sisa: number | null; sesudah: () => void
  otomatis: boolean; pakaiOtomatis: () => void
}) {
  async function mulai(a: BarisAntrean) {
    try { await tandai(kode, a.id, 'mulai'); sesudah() } catch { /* dicoba lagi saat poll */ }
  }
  // Maju otomatis sesudah lagu sebelumnya habis (YouTube berakhir, Spotify
  // lewat durasinya, layar skor selesai). Antrean kosong = penanda dibuang,
  // supaya lagu yang baru dipesan nanti tidak tiba-tiba diputar sendiri.
  const pertama = berikut[0]
  useEffect(() => {
    if (!otomatis) return
    if (!pertama) { pakaiOtomatis(); return }
    const t = window.setTimeout(() => { pakaiOtomatis(); void mulai(pertama) }, 1500)
    return () => window.clearTimeout(t)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otomatis, pertama?.id])
  // 5 Okt 2026: latar hidup (aurora + partikel), kartu kaca, masuk bertahap.
  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col px-10 py-8">
      <LatarHidup />
      <div className="relative z-[1] flex items-start gap-6 tv-naik">
        <div className="flex-1 min-w-0">
          <div className="text-[13px] tracking-[0.32em] uppercase text-gold-1/90">
            {room.room?.outlet}
          </div>
          <h1 className="text-5xl font-semibold mt-2 truncate">{room.room?.nama}</h1>
          {room.room?.tamu && <div className="text-[15px] text-text-3 mt-1 truncate">Selamat datang, {room.room.tamu}</div>}
        </div>
        {/* Kode dipasang BESAR dan permanen di layar tunggu.
            Tamu yang baru datang tidak akan bertanya ke kapten; ia akan
            menyerah dan menyanyi lewat kapten seperti biasa. */}
        <div className="text-right rounded-3xl px-7 py-5 tv-kaca-emas">
          <div className="text-[12px] text-text-2 tracking-wide">Kode remote di HP</div>
          <div className="text-6xl font-bold tracking-[0.15em] tv-teks-emas mt-1">{kode}</div>
          <div className="text-[12px] text-text-3 mt-1">
            buka <span className="text-text-2">{location.host}/r</span>
          </div>
        </div>
      </div>

      <div className="relative z-[1] flex-1 grid place-items-center py-6">
        {berikut.length === 0 ? (
          <div className="text-center tv-naik" style={{ animationDelay: '120ms' }}>
            <div className="tv-cincin">
              <div className="w-20 h-20 rounded-full grid place-items-center tv-kaca-emas">
                <Music4 size={36} className="text-gold-1" />
              </div>
            </div>
            <p className="mt-6 text-[26px] font-medium">Antrean kosong</p>
            <p className="mt-2 text-[16px] text-text-3">Pilih lagu dari HP — masukkan kode di pojok kanan atas.</p>
          </div>
        ) : (
          <div className="w-full max-w-3xl">
            <div className="text-[12px] text-gold-1/80 tracking-[0.3em] uppercase mb-4 tv-naik">Berikutnya</div>
            {berikut.slice(0, 6).map((a, i) => (
              <button key={a.id} onClick={() => mulai(a)}
                style={{ animationDelay: `${80 + i * 70}ms` }}
                className={`tv-naik w-full text-left rounded-2xl px-5 py-4 mb-2.5 flex items-center gap-4 ${
                  i === 0 ? 'tv-kaca-emas' : 'tv-kaca'}`}>
                <div className={`w-9 text-center tabular-nums ${i === 0 ? 'text-gold-1 text-2xl font-bold' : 'text-text-3 text-lg'}`}>{i + 1}</div>
                {a.sampul
                  ? <img src={a.sampul} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" />
                  : <div className="w-14 h-14 rounded-xl shrink-0 grid place-items-center bg-white/5 border border-white/10">
                      <Music4 size={20} className="text-white/35" />
                    </div>}
                <div className="flex-1 min-w-0">
                  <div className={`${i === 0 ? 'text-[23px] font-semibold' : 'text-[19px] font-medium'} truncate`}>{a.judul}</div>
                  <div className="text-[13px] text-text-3 truncate">
                    {a.artis}{a.diminta_oleh ? ` · ${a.diminta_oleh}` : ''}
                  </div>
                </div>
                {a.sumber === 'youtube' && <span className="px-2 py-0.5 rounded-md text-[11px] bg-red-500/15 text-red-300 border border-red-500/30">YouTube</span>}
                {a.sumber === 'spotify' && <span className="px-2 py-0.5 rounded-md text-[11px] bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Spotify</span>}
                {i === 0 && <span className="px-3 py-1.5 rounded-xl bg-gold text-black text-[13px] font-semibold whitespace-nowrap">Mulai ▶</span>}
              </button>
            ))}
            {berikut.length > 6 && (
              <div className="text-[13px] text-text-3 mt-1">+{berikut.length - 6} lagu lagi di antrean</div>
            )}
            <div className="text-[12px] text-text-3 mt-3">
              Tekan lagu paling atas untuk mulai.
            </div>
          </div>
        )}
      </div>

      <div className="relative z-[1] flex items-center gap-2 text-[14px] text-text-2">
        <Clock size={15} className="text-gold-1/80" /> Sisa waktu <span className="tabular-nums font-semibold">{jam(sisa)}</span>
      </div>
    </div>
  )
}

// ── LAYAR MAIN ─────────────────────────────────────────────────────────────

type Hasil = ReturnType<Penilai['hasil']> | null

// TV yang berjalan di dalam APK karaoke (WebView) boleh memutar tanpa
// sentuhan — APK menyetel setMediaPlaybackRequiresUserGesture(false).

// ── 5 Okt 2026: LAPOR POSISI → PROGRESS BAR REMOTE ─────────────────────────
//
// Posisi dibaca tiap 1 dtk (murah: currentTime / getCurrentTime), tapi
// DIKIRIM hanya bila jalan/jeda berubah, posisi melompat > 1,5 dtk dari
// perkiraan (lompat, tempo, Spotify dibuka ulang), durasi/laju berubah, atau
// sudah 4 dtk (jalan) / 15 dtk (diam) sejak laporan terakhir. Remote
// menginterpolasi di antaranya. Server menyimpannya di memori
// (backend/handlers/posisi.go) — tidak ada tulisan database per laporan.
type BacaPosisi = () => { posisi: number; durasi: number; jalan: boolean; laju?: number } | null

function useLaporPosisi(kode: string, id: string, baca: BacaPosisi) {
  const bacaRef = useRef(baca)
  bacaRef.current = baca
  useEffect(() => {
    let akhir: { posisi: number; durasi: number; jalan: boolean; laju: number; pada: number } | null = null
    let terbang = false
    const detak = () => {
      if (terbang) return
      let p: ReturnType<BacaPosisi> = null
      try { p = bacaRef.current() } catch { p = null }
      if (!p || !isFinite(p.posisi)) return
      const posisi = Math.max(0, p.posisi)
      const durasi = isFinite(p.durasi) && p.durasi > 0 ? p.durasi : 0
      const laju = p.laju && isFinite(p.laju) && p.laju > 0 ? p.laju : 1
      const kini = performance.now()
      if (akhir) {
        const umur = kini - akhir.pada
        const kira = akhir.posisi + (akhir.jalan ? (umur / 1000) * akhir.laju : 0)
        const perlu = p.jalan !== akhir.jalan || Math.abs(posisi - kira) > 1.5
          || Math.abs(durasi - akhir.durasi) > 1 || laju !== akhir.laju
          || umur >= (p.jalan ? 4000 : 15000)
        if (!perlu) return
      }
      akhir = { posisi, durasi, jalan: p.jalan, laju, pada: kini }
      terbang = true
      // Gagal = diulang sendiri paling lambat 4 dtk lagi (umur laporan).
      laporPosisi(kode, id, { posisi, durasi, jalan: p.jalan, laju })
        .catch(() => {})
        .finally(() => { terbang = false })
    }
    const t = window.setInterval(detak, 1000)
    detak()
    return () => window.clearInterval(t)
  }, [kode, id])
}

// ── 3 Okt 2026: LAGU UPLOAD DENGAN STEM ────────────────────────────────────
//
// Vokal on/off, musik on/off, nada ±6 (worklet tulisan sendiri), tempo,
// volume, jeda — semuanya dari remote lewat kolom antrean yang di-poll.
// Lihat lib/mesinStem.ts untuk tiga mode (stem / tukar / asli).
function Main({ kode, baris, berikut, sesudah }: {
  kode: string; baris: BarisAntrean; berikut: BarisAntrean[]; sesudah: () => void
}) {
  // 21 Okt 2026: bacaan latin lirik non-latin (lib/lirik.ts romanisasiLirik).
  const romFn = useCallback((tx: string[]) => romanisasiLirik(kode, tx), [kode])
  const vidRef = useRef<HTMLVideoElement>(null)
  const [berkas, setBerkas] = useState<BerkasLagu | null>(null)
  const [galat, setGalat] = useState('')
  // 5 Okt 2026: posisi lagu TIDAK lagi disimpan di state tiap frame (dulu
  // seluruh layar main digambar ulang 60×/dtk hanya untuk lirik). Lirik
  // membaca jamnya sendiri lewat `waktuLirik`; di sini cuma penanda
  // "20 detik terakhir" yang berubah sekali per lagu.
  const [dekatAkhir, setDekatAkhir] = useState(false)
  const dekatAkhirRef = useRef(false)
  const [intro, setIntro] = useState(false)
  const introSudah = useRef(false)
  const [hasil, setHasil] = useState<Hasil>(null)
  const [mikNyala, setMikNyala] = useState(false)
  const [kemampuan, setKemampuan] = useState<Kemampuan | null>(null)
  const [mulaiDitekan, setMulaiDitekan] = useState(false)
  const [tertahan, setTertahan] = useState(false)
  const [tanpaCors, setTanpaCors] = useState(false)

  const mesin = useRef<MesinStem | null>(null)
  const mik = useRef<DengarMik | null>(null)
  const nilai = useRef<Penilai | null>(null)
  // 8 Okt 2026 — ulang (loop) dari remote: saat video habis, kembali ke awal
  // tanpa menandai selesai. Ref supaya handler onEnded membaca nilai terbaru.
  const ulangRef = useRef(!!baris.ulang)
  useEffect(() => { ulangRef.current = !!baris.ulang }, [baris.ulang])
  const habis = () => {
    if (ulangRef.current && mesin.current) { mesin.current.ulangDariAwal(); return }
    void selesai()
  }

  // ── Muat berkas lagu ──
  useEffect(() => {
    let batal = false
    if (!baris.song_id) { setGalat('Item antrean ini bukan lagu katalog'); return }
    ambilBerkas(kode, baris.song_id)
      .then(b => { if (!batal) setBerkas(b) })
      .catch(e => { if (!batal) setGalat(pesanGalat(e)) })
    return () => { batal = true }
  }, [kode, baris.song_id])

  // Nilai `mutu` yang sah: utama | hemat | pratinjau | instrumen | vokal.
  // 'instrumen', BUKAN 'instrumental' — dijaga ck_kr_file_mutu.
  const trek = berkas?.trek ?? []
  const bukanStem = (m: string) => m !== 'instrumen' && m !== 'vokal'
  const video = trek.find(t => t.jenis === 'video' && t.mutu === 'utama')
    ?? trek.find(t => t.jenis === 'video' && bukanStem(t.mutu))
  const audioUtama = trek.find(t => t.jenis === 'audio' && t.mutu === 'utama')
    ?? trek.find(t => t.jenis === 'audio' && bukanStem(t.mutu))
  const inst = trek.find(t => t.mutu === 'instrumen')
  const vok = trek.find(t => t.mutu === 'vokal')
  const asli = video ?? audioUtama
  // 26 Okt: video vendor lama (kanal_vokal) tanpa stem → vokal on/off lewat kanal stereo.
  const kanalVokal = !inst && !vok ? (video?.kanal_vokal ?? null) : null
  const mode: ModeAudio = inst && vok ? 'stem' : inst && asli ? 'tukar' : kanalVokal ? 'kanal' : 'asli'
  // Sumber elemen <video> di layar = jam utama lirik & skor.
  //   stem + video  → video (dibisukan), stem mengikuti
  //   stem tanpa video → stem instrumen sendiri jadi jam
  //   tukar / asli  → berkas asli
  const srcMaster = mode === 'stem' ? (video ?? inst) : asli
  const masterBisu = mode === 'stem' && !!video
  // 26 Okt malam: proxy lokal TV (127.0.0.1:8091) yang tidak menjawab tidak
  // selalu memicu onError (permintaan menggantung) → kalau metadata video
  // perpustakaan lama belum terbaca dalam 8 dtk, pindah ke alamat langsung.
  useEffect(() => {
    const lama = !!srcMaster?.path_cadangan
    if (!lama || tanpaCors) return
    const t = window.setTimeout(() => {
      const v = vidRef.current
      if (v && v.readyState < 1) {
        mesin.current?.tutup(); mesin.current = null
        setMulaiDitekan(false); setTertahan(false); setTanpaCors(true)
      }
    }, 8000)
    return () => window.clearTimeout(t)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baris.id, tanpaCors, srcMaster?.path])

  // ── Nyalakan mesin audio ──
  //
  // AudioContext butuh tindakan orang di peramban biasa. Di APK TV boleh
  // langsung; di peramban dicoba juga, dan kalau tertahan tampil tombol.
  const nyalakan = useCallback(async () => {
    const v = vidRef.current
    if (!v || !srcMaster) return
    // 26 Okt: video tanpa CORS (server vendor lama) — diputar langsung oleh
    // elemen <video>, tanpa mesin Web Audio (yang membisukan media lintas asal).
    if (tanpaCors) {
      v.muted = false
      v.volume = Math.max(0, Math.min(1, (baris.volume ?? 100) / 100))
      try { await v.play(); setTertahan(false); setMulaiDitekan(true) } catch { setTertahan(true) }
      return
    }
    if (mesin.current) {
      // Sudah dibuat tapi tertahan peramban → tekan = coba lagi.
      const jalan = await mesin.current.mulai()
      setTertahan(!jalan)
      if (jalan) setMulaiDitekan(true)
      return
    }
    setMulaiDitekan(true)
    const ikut: { url: string; peran: 'inst' | 'voc' | 'utama' }[] = []
    if (mode === 'stem') {
      if (video && inst) ikut.push({ url: alamat(inst.path), peran: 'inst' })
      if (vok) ikut.push({ url: alamat(vok.path), peran: 'voc' })
    } else if (mode === 'tukar' && inst) {
      ikut.push({ url: alamat(inst.path), peran: 'inst' })
    }
    const m = new MesinStem({
      master: v, mode,
      peranMaster: masterBisu ? null : (mode === 'stem' ? 'inst' : 'utama'),
      ikut,
      kanalVokal,
    })
    mesin.current = m
    // 22 Okt: video ber-intro cerita panjang → mulai beberapa detik sebelum lagu.
    m.setMulai(potongRef.current)
    const k = await m.siapkan()
    m.setVokal(baris.vokal_panduan)
    m.setMusik(baris.musik_nyala ?? true)
    m.setNada(baris.geser_nada)
    m.setTempo(baris.tempo_persen)
    m.setVolume(baris.volume ?? 100)
    setKemampuan({ ...k })
    void laporPemutar(kode, baris.id, {
      // 'kanal' dilaporkan sebagai 'tukar' (kemampuannya sama: vokal on/off, musik off = bisu).
      mode: k.mode === 'kanal' ? 'tukar' : k.mode, vokal_bisa: k.vokalBisa, musik_bisa: k.musikBisa, nada_bisa: k.nadaBisa,
      tempo_bisa: k.tempoBisa, jeda_bisa: true, volume_bisa: true,
      pesan: k.mode === 'asli' ? 'Lagu belum diproses — vokal tidak bisa dipisah'
        : k.mode === 'tukar' ? 'Stem vokal belum ada — musik off membisukan semua'
        : '',
    }).catch(() => {})
    // 26 Okt: lagu vendor lama (kanal) — ukur isi kanal kiri/kanan 15 dtk setelah
    // mulai, laporkan ke server (kr_antrean.pemutar.pesan) untuk diagnosa vokal.
    if (k.mode === 'kanal') {
      window.setTimeout(async () => {
        const u = await m.ukurKanal(3000)
        if (!u) return
        void laporPemutar(kode, baris.id, {
          mode: 'tukar', vokal_bisa: true, musik_bisa: true, nada_bisa: k.nadaBisa,
          tempo_bisa: k.tempoBisa, jeda_bisa: true, volume_bisa: true,
          pesan: `kanal L=${u.l} R=${u.r} korelasi=${u.kor}` + (u.l + u.r < 0.001 ? ' (SENYAP — CORS?)' : ''),
        }).catch(() => {})
      }, 15000)
    }

    nilai.current = new Penilai({
      mode: berkas?.mode_skor ?? 'irama',
      melodi: berkas?.melodi ?? null,
      offsetDetik: berkas?.melodi_offset.detik,
      offsetSemitone: berkas?.melodi_offset.semitone,
      geserNada: baris.geser_nada,
    })
    try {
      mik.current = await DengarMik.buka()
      setMikNyala(true)
    } catch {
      setMikNyala(false)
    }
    if (baris.jeda) return
    const jalan = await m.mulai()
    setTertahan(!jalan)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baris.id, berkas, mode, srcMaster, masterBisu, tanpaCors])

  // Mulai sendiri begitu berkas siap — 6 Okt (Zaldy: "ketika play di remote
  // otomatis di layar playernya mulai juga"): SELALU dicoba, bukan hanya di
  // APK / sesudah ada sentuhan. PC TV yang Chrome-nya diizinkan autoplay
  // (pc-windows/izinkan-autoplay-karaoke.reg) langsung jalan; kalau peramban
  // tetap menahan, mesin.mulai() gagal → tertahan → tombol "Tekan untuk
  // mulai" muncul seperti sebelumnya.
  useEffect(() => {
    if (!berkas || !srcMaster || mulaiDitekan) return
    const t = window.setTimeout(() => { void nyalakan() }, 300)
    return () => window.clearTimeout(t)
  }, [berkas, srcMaster, mulaiDitekan, nyalakan])

  // ── Perintah dari remote (kolom antrean, di-poll) ──
  useEffect(() => { mesin.current?.setNada(baris.geser_nada) }, [baris.geser_nada])
  useEffect(() => { mesin.current?.setTempo(baris.tempo_persen) }, [baris.tempo_persen])
  useEffect(() => { mesin.current?.setVokal(baris.vokal_panduan) }, [baris.vokal_panduan])
  useEffect(() => { mesin.current?.setMusik(baris.musik_nyala ?? true) }, [baris.musik_nyala])
  useEffect(() => {
    if (mesin.current) { mesin.current.setVolume(baris.volume ?? 100); return }
    // 26 Okt: tanpa mesin (video tanpa CORS) — volume langsung di elemen video.
    const v = vidRef.current
    if (v) v.volume = Math.max(0, Math.min(1, (baris.volume ?? 100) / 100))
  }, [baris.volume, tanpaCors])
  useEffect(() => {
    if (mesin.current) { mesin.current.jeda(!!baris.jeda); return }
    const v = vidRef.current
    if (v && tanpaCors) { if (baris.jeda) v.pause(); else v.play().catch(() => {}) }
  }, [baris.jeda, tanpaCors])

  // ── Posisi untuk progress bar remote — jam = elemen <video> master ──
  useLaporPosisi(kode, baris.id, () => {
    const v = vidRef.current
    if (!v || hasil) return null
    return {
      posisi: v.currentTime, durasi: v.duration || (baris.durasi_detik ?? 0),
      jalan: mulaiDitekan && !tertahan && !v.paused && !v.ended, laju: v.playbackRate,
    }
  })

  // ── Lirik karaoke (sapuan) — jam = elemen <video> master ──
  const barisLirik = useMemo(() => susunDariBerkas(berkas as BerkasBerkata | null), [berkas])
  // 22 Okt (Zaldy): "auto crop video jadi mulai beberapa detik sebelum lagu" —
  // lihat mulaiLewatiIntro. Berkas tidak diubah; pemutar mulai dari detik ini.
  const potong = useMemo(() => mulaiLewatiIntro(barisLirik), [barisLirik])
  const potongRef = useRef(0)
  potongRef.current = potong
  const waktuLirik = useCallback(() => vidRef.current?.currentTime ?? 0, [])

  // Kartu judul sekali, saat lagu benar-benar mulai (bukan di balik tombol).
  useEffect(() => {
    if (mulaiDitekan && !tertahan && !introSudah.current) { introSudah.current = true; setIntro(true) }
  }, [mulaiDitekan, tertahan])

  // ── Denyut: cuplikan mikrofon + penanda 20 detik terakhir ──
  useEffect(() => {
    let hidup = true
    const denyut = () => {
      if (!hidup) return
      const v = vidRef.current
      if (v) {
        const t = v.currentTime
        const d = v.duration
        const dekat = isFinite(d) && d > 40 && t > 5 && d - t <= 20
        if (dekat !== dekatAkhirRef.current) { dekatAkhirRef.current = dekat; setDekatAkhir(dekat) }
        if (mik.current && nilai.current && !v.paused) {
          const jalan = adaLirikAktif(berkas, t)
          nilai.current.suap(mik.current.baca(), t, jalan)
        }
      }
      requestAnimationFrame(denyut)
    }
    requestAnimationFrame(denyut)
    return () => { hidup = false }
  }, [berkas])

  // ── Selesai ──
  const selesai = useCallback(async () => {
    const p = nilai.current
    const h = p ? p.hasil() : null
    setHasil(h)
    mik.current?.tutup(); mik.current = null
    if (h && baris.song_id) {
      try {
        await kirimSkor(kode, {
          antrean_id: baris.id, song_id: baris.song_id,
          nama: baris.diminta_oleh || 'Tamu',
          skor: h.skor, mode: h.mode, rincian: h.rincian,
          detik_bersuara: h.detikBersuara,
          geser_nada: baris.geser_nada, tempo_persen: baris.tempo_persen,
        })
      } catch { /* skor gagal terkirim tidak boleh menahan lagu berikutnya */ }
    }
    try { await tandai(kode, baris.id, 'selesai') } catch { /* diulang saat poll */ }
  }, [kode, baris])

  useEffect(() => () => { mesin.current?.tutup(); mik.current?.tutup() }, [])

  if (galat) return <Layar><p className="text-red-400">{galat}</p></Layar>
  if (!berkas) return <Layar><Loader2 className="animate-spin" /></Layar>

  if (hasil) {
    return <LayarSkor hasil={hasil} baris={baris} berikut={berikut}
      lanjut={() => { setHasil(null); sesudah() }} />
  }

  const musikNyala = baris.musik_nyala ?? true
  return (
    <div className="min-h-screen relative bg-black overflow-hidden">
      {/* Lagu audio-saja: latar hidup supaya layar tidak hitam polos. */}
      {!video && (
        <>
          <LatarHidup />
          <div className="absolute inset-0 grid place-items-center pb-[24vh] pointer-events-none">
            <div className="tv-cincin" style={{ width: 180, height: 180 }}>
              <Music4 size={84} className="text-white/15" />
            </div>
          </div>
        </>
      )}
      {srcMaster ? (
        <video key={tanpaCors ? 'polos' : 'cors'} ref={vidRef} src={alamat(tanpaCors && srcMaster.path_cadangan ? srcMaster.path_cadangan : srcMaster.path)} onEnded={habis}
          onError={() => {
            // 26 Okt (Fenix, perpustakaan lama): server vendor tidak mengirim CORS →
            // muat ulang tanpa crossOrigin. Vokal on/off tidak tersedia, lagu tetap jalan.
            if (!tanpaCors && (/^https?:\/\//.test(srcMaster.path) || srcMaster.path_cadangan)) {
              mesin.current?.tutup(); mesin.current = null
              setMulaiDitekan(false); setTertahan(false); setTanpaCors(true)
            }
          }}
          onLoadedMetadata={(e) => {
            const t = potongRef.current
            if (t > 0 && e.currentTarget.currentTime < 1) { try { e.currentTarget.currentTime = t } catch { /* */ } }
          }}
          muted={masterBisu}
          // 26 Okt (Zaldy: "videonya kotak, video lama pada gepeng"): video vendor
          // lama (4:3 / anamorfik) direntangkan penuh layar seperti di sistem lama;
          // video kita tetap proporsional.
          className={`absolute inset-0 w-full h-full ${srcMaster.path_cadangan ? 'object-fill' : 'object-contain'} ${video ? '' : 'opacity-0'}`}
          playsInline crossOrigin={tanpaCors ? undefined : 'anonymous'} preload="auto" />
      ) : (
        <div className="absolute inset-0 grid place-items-center text-text-3 text-[15px]">
          Lagu ini belum punya berkas media di outlet ini.
        </div>
      )}

      {/* ── Tombol mulai (peramban menahan suara sebelum ada sentuhan) ── */}
      {(!mulaiDitekan || tertahan) && srcMaster && (
        <button onClick={nyalakan}
          className="absolute inset-0 z-20 grid place-items-center bg-black/70 backdrop-blur-sm">
          <div className="text-center">
            <div className="text-[13px] text-text-3 uppercase tracking-wider">Siap</div>
            <div className="text-4xl font-semibold mt-2">{berkas.judul}</div>
            <div className="text-[16px] text-text-3 mt-1">{berkas.artis}</div>
            <div className="mt-8 inline-block px-10 py-4 rounded-2xl bg-gold text-black
                            text-[17px] font-semibold">Tekan untuk mulai</div>
            <div className="mt-4 text-[12px] text-text-3 max-w-sm mx-auto leading-relaxed">
              Suara dan mikrofon baru bisa dinyalakan setelah tombol ini ditekan —
              itu aturan peramban, bukan setelan yang bisa dimatikan.
            </div>
          </div>
        </button>
      )}

      {baris.jeda && mulaiDitekan && (
        <div className="absolute inset-0 z-[15] grid place-items-center pointer-events-none">
          <div className="px-8 py-4 rounded-2xl bg-black/70 text-3xl font-semibold tracking-wide">⏸ Dijeda</div>
        </div>
      )}

      {/* ── Lirik: gaya karaoke (sapuan biru) ── */}
      <div className="lk-panel">
        {/* 26 Okt (Zaldy): tanpa lirik berjalan → tidak ada tulisan apa pun
            (video karaoke lama sudah membawa lirik di gambarnya). */}
        {barisLirik.length > 0 && <LirikKaraoke baris={barisLirik} waktu={waktuLirik} romanisasi={romFn} />}
      </div>

      {intro && <KartuMasuk judul={berkas.judul} artis={berkas.artis} penyanyi={baris.diminta_oleh}
        selesai={() => setIntro(false)} />}
      <BerikutnyaPojok baris={berikut[0]} tampil={dekatAkhir && !intro} />

      {/* ── Bilah keadaan ── */}
      <div className="absolute top-0 inset-x-0 z-10 px-8 py-4 flex items-start gap-4 pointer-events-none
                      bg-gradient-to-b from-black/70 to-transparent">
        <div className="pt-1.5"><Equalizer jalan={mulaiDitekan && !tertahan && !baris.jeda} /></div>
        <div className="flex-1 min-w-0">
          <div className="text-[19px] font-semibold truncate">{berkas.judul}</div>
          <div className="text-[13px] text-text-3 truncate">
            {berkas.artis}{baris.diminta_oleh ? ` · ${baris.diminta_oleh}` : ''}
          </div>
        </div>
        {/* 26 Okt (Zaldy): tanda Nada/Vokal/Musik/Vol/Skor di layar TV disembunyikan —
            pengaturannya tetap dari remote. */}
      </div>

      {/* 26 Okt (Zaldy): catatan keterbatasan (lirik belum sinkron, skor tanpa acuan,
          dll.) tidak lagi ditampilkan di layar tamu. */}
    </div>
  )
}

// ── 3 Okt 2026: YOUTUBE (IFrame Player resmi) ──────────────────────────────
//
// Hanya IFrame Player API resmi — tidak ada unduhan, tidak ada ekstraksi
// audio. Musik off = mute, volume & jeda jalan lewat API pemutar.
//
// 5 Okt 2026 — VOKAL & NADA LEWAT EKSTENSI "EL AUDIO" (hanya PC Chrome):
// suara iframe lintas-origin tidak bisa diolah halaman, jadi ekstensi yang
// menangkap suara tab (tabCapture) dan memutarnya ulang lewat pengurang vokal
// mid/side + geser nada (lihat karaoke-app/ekstensi-chrome). Halaman ini cuma
// mengirim keinginan {youtube, vokal, nada} dan melaporkan kemampuan ke remote
// sesuai status ekstensi. Tanpa ekstensi / capture belum aktif → seperti
// dulu: vokal & nada mati di remote.

type PemainYT = {
  playVideo(): void; pauseVideo(): void; mute(): void; unMute(): void
  setVolume(v: number): void; getPlayerState(): number; destroy(): void
  // 5 Okt 2026: jam lirik & "berikutnya" di 20 detik terakhir.
  getCurrentTime(): number; getDuration(): number
  // 8 Okt 2026: ulang (loop) — kembali ke awal saat video habis.
  seekTo(detik: number, allowSeekAhead?: boolean): void
}
type NamaRuangYT = {
  Player: new (el: HTMLElement, opt: {
    videoId: string; width?: string; height?: string
    playerVars?: Record<string, string | number>
    events?: {
      onReady?: (e: { target: PemainYT }) => void
      onStateChange?: (e: { data: number; target: PemainYT }) => void
      onError?: (e: { data: number }) => void
    }
  }) => PemainYT
}
type JendelaYT = Window & { YT?: NamaRuangYT; onYouTubeIframeAPIReady?: () => void }

// ── 26 Okt 2026: PEMUTAR YOUTUBE LEWAT PERANTARA ───────────────────────────
// TV Fenix membuka halaman lewat proxy lokal http://127.0.0.1:8091 (vokal
// on/off lagu lama). YouTube menolak disematkan dari asal itu ("pemilik video
// tidak mengizinkan…"), padahal video yang sama jalan dari asal https pusat.
// Di asal lokal, YouTube disematkan lewat /yt-relay.html milik pusat; kendali
// & posisi lewat postMessage. Antarmukanya sama dengan PemainYT.
const PUSAT_KARAOKE = 'https://karaoke.elgroupapp.com'
const pakaiPerantaraYT = () =>
  location.origin !== PUSAT_KARAOKE &&
  (location.hostname === '127.0.0.1' || location.hostname === 'localhost' || location.hostname.endsWith('.localhost'))

type OpsiPemainYT = ConstructorParameters<NamaRuangYT['Player']>[1]

class PemainPerantara implements PemainYT {
  private f: HTMLIFrameElement
  private s = -1
  private t = 0
  private d = 0
  private tPada = 0
  private dengar: (ev: MessageEvent) => void

  constructor(el: HTMLElement, opt: OpsiPemainYT) {
    const pv: Record<string, string | number> = { ...(opt.playerVars ?? {}) }
    delete pv.origin
    const q = new URLSearchParams({ v: opt.videoId, pv: JSON.stringify(pv) })
    const f = document.createElement('iframe')
    f.src = `${PUSAT_KARAOKE}/yt-relay.html?${q}`
    f.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture'
    f.style.cssText = 'border:0;width:100%;height:100%;display:block'
    el.replaceWith(f)
    this.f = f
    this.dengar = (ev: MessageEvent) => {
      if (ev.source !== f.contentWindow) return
      const m = ev.data as { yt?: string; data?: number; s?: number; t?: number; d?: number } | null
      if (!m || typeof m.yt !== 'string') return
      const catat = (t?: number, d?: number) => {
        if (typeof t === 'number') { this.t = t; this.tPada = performance.now() }
        if (typeof d === 'number' && d > 0) this.d = d
      }
      if (m.yt === 'siap') opt.events?.onReady?.({ target: this })
      else if (m.yt === 'keadaan' && typeof m.data === 'number') {
        this.s = m.data; catat(m.t, m.d)
        opt.events?.onStateChange?.({ data: m.data, target: this })
      } else if (m.yt === 'galat') opt.events?.onError?.({ data: m.data ?? -1 })
      else if (m.yt === 'detak') { if (typeof m.s === 'number') this.s = m.s; catat(m.t, m.d) }
    }
    window.addEventListener('message', this.dengar)
  }
  private kirim(cmd: string, ...args: unknown[]) {
    this.f.contentWindow?.postMessage({ yt: 'perintah', cmd, args }, PUSAT_KARAOKE)
  }
  playVideo() { this.kirim('playVideo') }
  pauseVideo() { this.kirim('pauseVideo') }
  mute() { this.kirim('mute') }
  unMute() { this.kirim('unMute') }
  setVolume(v: number) { this.kirim('setVolume', v) }
  seekTo(detik: number, allowSeekAhead?: boolean) {
    this.kirim('seekTo', detik, allowSeekAhead ?? true)
    this.t = detik; this.tPada = performance.now()
  }
  getPlayerState() { return this.s }
  // Posisi dikirim tiap 250 ms — di antaranya diperkirakan supaya lirik halus.
  getCurrentTime() {
    if (this.s !== 1) return this.t
    const x = this.t + (performance.now() - this.tPada) / 1000
    return this.d > 0 ? Math.min(x, this.d) : x
  }
  getDuration() { return this.d }
  destroy() { window.removeEventListener('message', this.dengar); this.f.remove() }
}

let janjiYT: Promise<NamaRuangYT> | null = null
function muatYT(): Promise<NamaRuangYT> {
  if (pakaiPerantaraYT()) return Promise.resolve({ Player: PemainPerantara } as unknown as NamaRuangYT)
  const w = window as JendelaYT
  if (w.YT?.Player) return Promise.resolve(w.YT)
  if (!janjiYT) {
    janjiYT = new Promise((res, rej) => {
      const lama = w.onYouTubeIframeAPIReady
      w.onYouTubeIframeAPIReady = () => { lama?.(); if (w.YT) res(w.YT) }
      const s = document.createElement('script')
      s.src = 'https://www.youtube.com/iframe_api'
      s.async = true
      s.onerror = () => { janjiYT = null; rej(new Error('Gagal memuat pemutar YouTube — periksa internet TV')) }
      document.head.appendChild(s)
    })
  }
  return janjiYT
}

function MainYouTube({ kode, baris, berikut, sesudah }: {
  kode: string; baris: BarisAntrean; berikut: BarisAntrean[]; sesudah: () => void
}) {
  // 21 Okt 2026: bacaan latin lirik non-latin (lib/lirik.ts romanisasiLirik).
  const romFn = useCallback((tx: string[]) => romanisasiLirik(kode, tx), [kode])
  const wadah = useRef<HTMLDivElement>(null)
  const pemain = useRef<PemainYT | null>(null)
  const [galat, setGalat] = useState('')
  const [tertahan, setTertahan] = useState(false)
  const [siap, setSiap] = useState(false)
  const selesaiRef = useRef(false)
  // 8 Okt 2026 — ulang (loop) dari remote.
  const ulangRef = useRef(!!baris.ulang)
  useEffect(() => { ulangRef.current = !!baris.ulang }, [baris.ulang])
  const vid = baris.ref ?? ''

  // ── 5 Okt 2026: LIRIK LRCLIB (dicari backend) + kartu judul + berikutnya ──
  //
  // Video karaoke YouTube sering sudah membawa lirik di gambarnya, dan intro
  // versi karaoke bisa beda beberapa detik dari rekaman aslinya → lirik bisa
  // digeser (±0,5 dtk) atau disembunyikan. Disimpan di TV per video.
  const [lirik, setLirik] = useState<BarisKaraoke[] | null>(null)
  const [lirikDurasi, setLirikDurasi] = useState(0)
  // 27 Okt: lirik dimuat ulang saat PC karaoke selesai menyelaraskan di tengah
  // lagu (lirik_tampil berubah lewat polling antrean) — baris selaras
  // menggantikan tebakan LRCLIB tanpa menunggu lagu berikutnya.
  useEffect(() => {
    let hidup = true
    ambilLirikLuarRinci(kode, baris.id)
      .then(r => { if (hidup) { setLirik(r.baris); setLirikDurasi(r.durasi) } })
      .catch(() => { if (hidup) setLirik([]) })
    return () => { hidup = false }
  }, [kode, baris.id, baris.lirik_tampil])
  const { setel, ubahSetel, offsetRef } = useSetelanLirik(`youtube:${vid}`)
  // 21 Okt 2026 — Zaldy: "lirik ga sync sama sekali kalau play by YouTube".
  // Lirik LRCLIB milik rekaman asli; video karaoke YouTube intronya beda.
  // (a) geser lirik dari TABLET REMOTE, disimpan per video di server
  //     (baris.lirik_geser) — ditambahkan ke geseran lokal TV (tombol [ ]).
  // (b) durasi video vs durasi rekaman LRCLIB beda > 8 dtk = versi lain
  //     (intro/tempo beda) → lirik disembunyikan, bukan ditampilkan salah.
  const geserServerRef = useRef(0)
  const [durasiVideo, setDurasiVideo] = useState(0)
  const selisihVersi = lirikDurasi > 0 && durasiVideo > 0 ? Math.abs(durasiVideo - lirikDurasi) : 0
  // 21 Okt 2026 malam — Zaldy: "teks masih beberapa ada yang delay kalau dari
  // YouTube, make sure benar-benar". Video karaoke hampir selalu beda durasi
  // dengan rekaman LRCLIB karena INTRO-nya (kartu judul, hitungan, intro
  // dipotong). Selisih 1–8 dtk dipakai sebagai geseran OTOMATIS: video lebih
  // panjang Δ → lirik dimundurkan Δ, lebih pendek → dimajukan. Geseran dari
  // remote (per video, server) selalu menang atas tebakan ini.
  // 22 Okt 2026 — Zaldy: "yang dari putar YouTube masih delay liriknya".
  // Tebakan di atas hanya benar untuk VIDEO KARAOKE/LIRIK (selisihnya kartu
  // judul di DEPAN). Official MV / video musik biasa umumnya lebih panjang di
  // BELAKANG (end screen, adegan penutup) — menggeser lirik sebesar selisih itu
  // justru membuatnya TELAT. Jadi geser otomatis hanya untuk video karaoke;
  // MV memakai waktu LRCLIB apa adanya (geser manual dari remote tetap bisa).
  // 27 Okt: + kanal yang mengunggah rip VCD karaoke dengan lirik terbakar di
  // gambar walau judulnya "Official Music Video" (GP Musikpedia, Blackboard).
  // Dipakai hanya untuk geser otomatis; tampil/tidaknya lirik ditentukan lirik_tampil.
  const videoKaraoke = /karaoke|lirik|lyrics?|minus\s*one|instrumental|backing\s*track|卡拉|ktv|伴奏|노래방|カラオケ|musikpedia|blackboard/i
    .test(`${baris.judul ?? ''} ${baris.artis ?? ''}`)
  const geserAuto = videoKaraoke && lirikDurasi > 0 && durasiVideo > 0 && selisihVersi >= 1 && selisihVersi <= 8
    ? Math.round((durasiVideo - lirikDurasi) * 10) / 10 : 0
  const geserManual = baris.lirik_geser != null && Number(baris.lirik_geser) !== 0
  const geserEfektif = geserManual ? Number(baris.lirik_geser) : geserAuto
  // 26 Okt 2026 — Zaldy: "masih ada yg gaada lyricnya kl dr youtube" (Mrs.
  // GREEN APPLE シンデレラ MV 291 dtk vs lirik 275 dtk → disembunyikan).
  // Official MV hampir selalu lebih panjang (adegan pembuka/penutup), jadi
  // batas 8 dtk hanya untuk video karaoke; MV baru disembunyikan kalau beda
  // > 45 dtk. Geser manual dari remote = staf sudah menyamakan → selalu tampil.
  // 27 Okt — Zaldy: "yang dari YouTube jangan dikasih lirik kalau nggak yakin
  // pas … nanti live awal-awal banyak customer yang error, nggak mungkin mereka
  // yang test". Tebakan judul/durasi tetap meleset (Alda "Patah Jadi Dua" MV:
  // durasi cocok, tapi lirik VCD sudah terbakar di gambar). Jadi: lirik kita
  // untuk YouTube MATI sebagai bawaan. Tampil HANYA bila staf menyalakannya
  // untuk video ini dari remote (baris.lirik_tampil — per video, semua room).
  const versiBeda = !baris.lirik_tampil
  useEffect(() => { geserServerRef.current = geserEfektif }, [geserEfektif])
  const stateRef = useRef(-1)
  const jamYT = useMemo(() => buatJamHalus(() => {
    const p = pemain.current
    if (!p) return null
    try { return { t: p.getCurrentTime(), main: stateRef.current === 1 } } catch { return null }
  }), [])
  const waktuLirik = useCallback(() => jamYT() - offsetRef.current - geserServerRef.current, [jamYT, offsetRef])
  const [main, setMain] = useState(false)
  const [intro, setIntro] = useState(false)
  const introSudah = useRef(false)
  const [dekatAkhir, setDekatAkhir] = useState(false)
  useEffect(() => {
    if (!siap) return
    const t = window.setInterval(() => {
      const p = pemain.current
      if (!p) return
      try {
        const d = p.getDuration(), c = p.getCurrentTime()
        setDekatAkhir(d > 40 && c > 5 && d - c <= 20)
        if (d > 0) setDurasiVideo(prev => (Math.abs(prev - d) > 0.5 ? d : prev))
      } catch { /* pemain sudah lepas */ }
    }, 1000)
    return () => window.clearInterval(t)
  }, [siap])

  const akhiri = useCallback(async (aksi: 'selesai' | 'lewati') => {
    if (selesaiRef.current) return
    selesaiRef.current = true
    try { await tandai(kode, baris.id, aksi) } catch { /* diulang saat poll */ }
    sesudah()
  }, [kode, baris.id, sesudah])

  // Posisi untuk progress bar remote. Buffering (3) = tidak maju.
  useLaporPosisi(kode, baris.id, () => {
    const p = pemain.current
    if (!p || !siap) return null
    return { posisi: p.getCurrentTime(), durasi: p.getDuration() || (baris.durasi_detik ?? 0), jalan: stateRef.current === 1 }
  })

  // ── EL Audio (ekstensi PC room) ──
  const ea = useElAudio()
  const efekAktif = ea.ada && ea.status === 'aktif'
  const nadaAktif = efekAktif && ea.nadaBisa

  // Laporan kemampuan ke remote — dikirim ulang setiap status ekstensi
  // berubah di tengah lagu (mis. staf baru menekan ikon EL Audio). Server
  // mengabaikan laporan yang isinya sama (pemutar IS DISTINCT FROM).
  useEffect(() => {
    void laporPemutar(kode, baris.id, {
      mode: 'youtube', vokal_bisa: efekAktif, musik_bisa: true, nada_bisa: nadaAktif,
      tempo_bisa: false, jeda_bisa: true, volume_bisa: true,
      pesan: efekAktif
        ? 'YouTube: vokal off memakai pengurang vokal — hasil tergantung lagu'
          + (nadaAktif ? '' : ' · geser nada tidak aktif di PC ini')
        : ea.ada
          ? 'YouTube: vokal & nada menunggu EL Audio diaktifkan di PC TV (klik ikon sekali)'
          : 'YouTube: vokal & nada hanya untuk lagu upload',
    }).catch(() => {})
  }, [kode, baris.id, efekAktif, nadaAktif, ea.ada])

  // Keinginan efek dikirim setiap berubah — juga saat capture belum aktif,
  // supaya ekstensi langsung memakai nilai terakhir begitu ikonnya ditekan.
  useEffect(() => {
    if (!ea.ada) return
    aturElAudio({ youtube: true, vokal: baris.vokal_panduan, nada: baris.geser_nada })
  }, [ea.ada, efekAktif, baris.vokal_panduan, baris.geser_nada])

  // Lagu selesai / komponen lepas → netral (lagu upload lewat apa adanya).
  useEffect(() => () => { aturElAudio({ youtube: false, vokal: true, nada: 0 }) }, [])

  // Sekali per lagu: coba mulai tanpa klik (biasanya ditolak Chrome — wajar;
  // petunjuk di layar yang meminta staf menekan ikon).
  const sudahMinta = useRef(false)
  useEffect(() => {
    if (!ea.ada || ea.status !== 'belum' || sudahMinta.current) return
    sudahMinta.current = true
    mintaMulaiElAudio()
  }, [ea.ada, ea.status])

  useEffect(() => {
    let hidup = true
    let periksa: number | undefined
    muatYT().then(YT => {
      if (!hidup || !wadah.current) return
      const el = document.createElement('div')
      wadah.current.innerHTML = ''
      wadah.current.appendChild(el)
      pemain.current = new YT.Player(el, {
        videoId: vid, width: '100%', height: '100%',
        playerVars: {
          autoplay: 1, controls: 0, rel: 0, modestbranding: 1, playsinline: 1,
          iv_load_policy: 3, fs: 0, disablekb: 1, origin: location.origin,
          // 26 Okt (Zaldy: "caption YouTube nabrak lirik"): teks terjemahan/CC
          // YouTube dimatikan — lirik kita sendiri yang tampil.
          cc_load_policy: 0,
        },
        events: {
          onReady: e => {
            if (!hidup) return
            setSiap(true)
            // Video yang memaksa CC tetap menyalakannya walau cc_load_policy 0 —
            // copot modul caption-nya (API tidak resmi, aman bila tidak ada).
            const cc = e.target as unknown as { unloadModule?: (m: string) => void }
            try { cc.unloadModule?.('captions'); cc.unloadModule?.('cc') } catch { /* */ }
            e.target.setVolume(Math.min(100, baris.volume ?? 100))
            if (baris.musik_nyala === false) e.target.mute(); else e.target.unMute()
            if (!baris.jeda) e.target.playVideo()
            // Peramban yang menahan autoplay bersuara: state tetap -1/5.
            periksa = window.setTimeout(() => {
              const st = pemain.current?.getPlayerState()
              if (hidup && !baris.jeda && st !== 1 && st !== 3) setTertahan(true)
            }, 3500)
          },
          onStateChange: e => {
            stateRef.current = e.data
            if (e.data === 1) {
              setTertahan(false)
              // Caption bisa termuat baru saat video mulai jalan — copot lagi.
              const cc = e.target as unknown as { unloadModule?: (m: string) => void }
              try { cc.unloadModule?.('captions'); cc.unloadModule?.('cc') } catch { /* */ }
              if (!introSudah.current) { introSudah.current = true; setIntro(true) }
            }
            if (e.data !== 3) setMain(e.data === 1)
            if (e.data === 0) {
              if (ulangRef.current) { try { e.target.seekTo(0, true); e.target.playVideo() } catch { /* */ } }
              else void akhiri('selesai')
            }
          },
          onError: e => {
            // 2 id salah · 5 HTML5 · 100 dihapus/privat · 101/150 tidak boleh disematkan
            setGalat(e.data === 101 || e.data === 150
              ? 'Pemilik video tidak mengizinkan diputar di luar YouTube — dilewati.'
              : `Video tidak bisa diputar (kode ${e.data}) — dilewati.`)
            // 26 Okt: kode galat ikut dilaporkan (kr_antrean.pemutar.pesan) supaya
            // terbaca dari server — layar TV hanya menampilkannya 6 detik.
            void laporPemutar(kode, baris.id, {
              mode: 'youtube', vokal_bisa: false, musik_bisa: true, nada_bisa: false,
              tempo_bisa: false, jeda_bisa: true, volume_bisa: true,
              pesan: `YouTube galat ${e.data} · asal ${location.origin}`,
            }).catch(() => {})
            window.setTimeout(() => { void akhiri('lewati') }, 6000)
          },
        },
      })
    }).catch(e => { if (hidup) setGalat(pesanGalat(e)) })
    return () => {
      hidup = false
      window.clearTimeout(periksa)
      try { pemain.current?.destroy() } catch { /* sudah lepas */ }
      pemain.current = null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vid])

  useEffect(() => {
    const p = pemain.current
    if (!p || !siap) return
    if (baris.jeda) p.pauseVideo(); else p.playVideo()
  }, [baris.jeda, siap])
  useEffect(() => {
    const p = pemain.current
    if (!p || !siap) return
    if (baris.musik_nyala === false) p.mute(); else p.unMute()
  }, [baris.musik_nyala, siap])
  useEffect(() => {
    if (pemain.current && siap) pemain.current.setVolume(Math.min(100, baris.volume ?? 100))
  }, [baris.volume, siap])

  return (
    <div className="min-h-screen relative bg-black overflow-hidden">
      <div ref={wadah} className="absolute inset-0 [&>iframe]:w-full [&>iframe]:h-full" />

      {/* ── Lirik LRCLIB, gaya karaoke, di atas video bagian bawah ── */}
      {lirik && lirik.length > 0 && !setel.sembunyi && !versiBeda && (
        <div className="lk-panel">
          <LirikKaraoke baris={lirik} waktu={waktuLirik} romanisasi={romFn} />
        </div>
      )}
      {!galat && (
        <KontrolLirik status={lirik === null ? 'cari' : !lirik.length ? 'tidak' : versiBeda ? 'beda' : 'ada'}
          setel={setel} ubah={ubahSetel} geserServer={geserEfektif}
          keterangan={versiBeda ? 'Lirik YouTube mati (bawaan) — nyalakan dari remote bila pas' : ''} />
      )}
      {intro && <KartuMasuk judul={baris.judul} artis={baris.artis} penyanyi={baris.diminta_oleh}
        sumber="youtube" selesai={() => setIntro(false)} />}
      <BerikutnyaPojok baris={berikut[0]} tampil={dekatAkhir && !intro} />

      <div className="absolute top-0 inset-x-0 z-10 px-8 py-4 flex items-start gap-4 pointer-events-none
                      bg-gradient-to-b from-black/70 to-transparent">
        <div className="pt-1.5"><Equalizer jalan={main && !baris.jeda} /></div>
        <div className="flex-1 min-w-0">
          <div className="text-[19px] font-semibold truncate">{baris.judul}</div>
          <div className="text-[13px] text-text-3 truncate">
            YouTube · {baris.artis}{baris.diminta_oleh ? ` · ${baris.diminta_oleh}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-3 text-[12px] flex-wrap justify-end">
          {nadaAktif && (
            <Tanda aktif={baris.geser_nada !== 0}>{nadaTampil(baris.nada_dasar, baris.geser_nada)}</Tanda>
          )}
          {efekAktif && (
            <Tanda aktif={!baris.vokal_panduan}>{baris.vokal_panduan ? 'Vokal ON' : 'Vokal OFF'}</Tanda>
          )}
          {baris.musik_nyala === false && <Tanda aktif warna="merah">Musik OFF (mute)</Tanda>}
          {baris.jeda && <Tanda aktif>Dijeda</Tanda>}
        </div>
      </div>
      {/* ── EL Audio: petunjuk kecil bila ekstensi ada tapi belum aktif ── */}
      {/* Petunjuk EL Audio dipindah ke atas (5 Okt): bagian bawah kini milik lirik. */}
      {ea.ada && !efekAktif && (
        <div className="absolute top-[78px] inset-x-0 z-10 px-10 pointer-events-none flex justify-center">
          <span className="px-3 py-1.5 rounded-lg bg-black/60 border border-amber-400/30 text-[12px]
                           text-amber-300/90 flex items-center gap-1.5">
            <AlertTriangle size={12} />
            {ea.status === 'menyiapkan'
              ? 'EL Audio menyiapkan vokal & nada…'
              : ea.status === 'galat'
                ? `EL Audio galat${ea.pesan ? ` (${ea.pesan})` : ''} — klik ikon EL Audio sekali lagi`
                : 'Klik ikon EL Audio sekali (atau Alt+Shift+K) untuk mengaktifkan vokal & nada'}
          </span>
        </div>
      )}
      {efekAktif && !baris.vokal_panduan && (
        <div className="absolute top-[78px] inset-x-0 z-10 px-10 pointer-events-none text-center text-[11px] text-text-3">
          Vokal off memakai pengurang vokal — hasilnya tergantung lagu
        </div>
      )}
      {galat && (
        <div className="absolute inset-x-0 bottom-10 z-20 text-center">
          <span className="px-5 py-3 rounded-xl bg-red-500/20 border border-red-500/40 text-red-200 text-[15px]">{galat}</span>
        </div>
      )}
      {tertahan && !galat && (
        <button onClick={() => { pemain.current?.playVideo(); if (baris.musik_nyala !== false) pemain.current?.unMute(); setTertahan(false) }}
          className="absolute inset-0 z-20 grid place-items-center bg-black/60">
          <div className="px-10 py-4 rounded-2xl bg-gold text-black text-[17px] font-semibold">Tekan untuk memutar</div>
        </button>
      )}
    </div>
  )
}

// ── 3 Okt 2026: SPOTIFY — DIPUTAR APLIKASI SPOTIFY DI KOMPUTER INI ─────────
//
// Keputusan pemilik: audio Spotify tidak lewat pemutar kita. Layar ini (yang
// berjalan di komputer karaoke) membuka spotify:track:<id> di aplikasi
// Spotify desktop. Lewat iframe tersembunyi supaya halaman ini tidak pindah;
// kalau peramban menahannya (butuh sentuhan), tombol besar tersedia, plus
// tautan open.spotify.com sebagai cadangan.
//
// Kita tidak tahu kapan Spotify selesai memutar → maju otomatis sesudah
// durasi trek (+5 dtk) bila diketahui; selain itu menunggu Selesai/Lewati
// dari remote.
function MainSpotify({ kode, baris, berikut, sesudah }: {
  kode: string; baris: BarisAntrean; berikut: BarisAntrean[]; sesudah: () => void
}) {
  // 21 Okt 2026: bacaan latin lirik non-latin (lib/lirik.ts romanisasiLirik).
  const romFn = useCallback((tx: string[]) => romanisasiLirik(kode, tx), [kode])
  const ref = baris.ref ?? ''
  const uri = uriSpotify(ref)
  const durasi = baris.durasi_detik ?? 0
  const [sisa, setSisa] = useState<number | null>(durasi > 0 ? durasi + 5 : null)
  const [dibuka, setDibuka] = useState(0)
  const selesaiRef = useRef(false)

  // ── 5 Okt 2026: LIRIK PERKIRAAN ──
  //
  // Posisi pemutaran aplikasi Spotify TIDAK bisa dibaca dari halaman ini.
  // Lirik LRCLIB berjalan dari jam sejak layar membuka Spotify, dikurangi
  // ±1,5 dtk waktu aplikasi memuat — PERKIRAAN. Meleset → geser ±0,5 dtk
  // atau sembunyikan (disimpan di TV per trek).
  const mulaiRef = useRef(performance.now())
  const [lirik, setLirik] = useState<BarisKaraoke[] | null>(null)
  const { setel, ubahSetel, offsetRef } = useSetelanLirik(`spotify:${ref}`)
  const waktuLirik = useCallback(
    () => (performance.now() - mulaiRef.current) / 1000 - JEDA_BUKA_SPOTIFY - offsetRef.current,
    [offsetRef])
  const [intro, setIntro] = useState(true)
  // Posisi untuk progress bar remote: PERKIRAAN yang sama dengan jam lirik
  // (sejak Spotify dibuka), tanpa offset lirik. Durasi dari data trek.
  useLaporPosisi(kode, baris.id, () => {
    const t = Math.max(0, (performance.now() - mulaiRef.current) / 1000 - JEDA_BUKA_SPOTIFY)
    return { posisi: durasi > 0 ? Math.min(t, durasi) : t, durasi, jalan: durasi <= 0 || t < durasi }
  })
  useEffect(() => {
    let hidup = true
    ambilLirikLuar(kode, baris.id)
      .then(b => { if (hidup) setLirik(b) })
      .catch(() => { if (hidup) setLirik([]) })
    return () => { hidup = false }
  }, [kode, baris.id])

  const buka = useCallback(() => {
    // iframe tersembunyi: halaman tidak berpindah; di APK (WebView) navigasi
    // iframe ke skema asing tidak merusak halaman utama.
    try {
      const f = document.createElement('iframe')
      f.style.display = 'none'
      f.src = uri.app
      document.body.appendChild(f)
      window.setTimeout(() => f.remove(), 4000)
    } catch { /* abaikan */ }
    mulaiRef.current = performance.now()
    setDibuka(n => n + 1)
  }, [uri.app])

  useEffect(() => {
    buka()
    void laporPemutar(kode, baris.id, {
      mode: 'spotify', vokal_bisa: false, musik_bisa: false, nada_bisa: false,
      tempo_bisa: false, jeda_bisa: false, volume_bisa: false,
      pesan: 'Diputar aplikasi Spotify di komputer karaoke',
    }).catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baris.id])

  useEffect(() => {
    if (sisa === null) return
    if (sisa <= 0) {
      if (!selesaiRef.current) {
        selesaiRef.current = true
        void tandai(kode, baris.id, 'selesai').catch(() => {}).finally(sesudah)
      }
      return
    }
    const t = window.setTimeout(() => setSisa(s => (s === null ? s : s - 1)), 1000)
    return () => window.clearTimeout(t)
  }, [sisa, kode, baris.id, sesudah])

  const adaLirik = !!lirik && lirik.length > 0 && !setel.sembunyi
  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col">
      <LatarHidup partikel={!adaLirik} />
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(900px 600px at 50% 0%, rgba(30,215,96,.22), transparent 60%)' }} />
      {baris.sampul && (
        <img src={baris.sampul} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15 blur-2xl scale-110 pointer-events-none" />
      )}
      <div className={`relative z-[1] flex-1 flex flex-col items-center justify-center px-10 text-center ${adaLirik ? 'pt-8' : ''}`}>
        <div className={`flex items-center gap-8 ${adaLirik ? 'text-left' : 'flex-col gap-0'}`}>
          {baris.sampul && (
            <img src={baris.sampul} alt=""
              className={`${adaLirik ? 'w-40 h-40' : 'w-64 h-64 mx-auto'} rounded-3xl object-cover shadow-2xl shrink-0`} />
          )}
          <div className={adaLirik ? 'min-w-0' : 'mt-6'}>
            <div className="text-[13px] tracking-[0.25em] uppercase text-emerald-400 flex items-center gap-2 justify-center">
              <Equalizer /> Spotify
            </div>
            <h1 className={`mt-2 font-semibold ${adaLirik ? 'text-4xl' : 'text-5xl'}`}>{baris.judul}</h1>
            <div className="mt-2 text-xl text-text-3">{baris.artis}{baris.diminta_oleh ? ` · ${baris.diminta_oleh}` : ''}</div>
          </div>
        </div>
        {!adaLirik && (
          <p className="mt-6 text-[14px] text-text-3 max-w-xl mx-auto">
            Lagu ini diputar oleh aplikasi Spotify di komputer karaoke ini. Vokal, nada, dan
            volume diatur dari aplikasi Spotify — bukan dari remote.
          </p>
        )}
        <div className={`${adaLirik ? 'mt-5' : 'mt-6'} flex items-center justify-center gap-3`}>
          <a href={uri.app} onClick={() => { mulaiRef.current = performance.now(); setDibuka(n => n + 1) }}
            className="px-8 py-4 rounded-2xl bg-emerald-500 text-black text-[17px] font-semibold">
            {dibuka > 1 ? 'Buka lagi di Spotify' : 'Putar di Spotify'}
          </a>
          <a href={uri.web} target="_blank" rel="noreferrer"
            className="px-6 py-4 rounded-2xl border border-white/15 text-[14px] text-text-2">
            open.spotify.com
          </a>
          <button onClick={() => { if (!selesaiRef.current) { selesaiRef.current = true; void tandai(kode, baris.id, 'selesai').catch(() => {}).finally(sesudah) } }}
            className="px-5 py-4 rounded-2xl bg-white/5 border border-white/10 text-[13px]">
            Selesai — lagu berikutnya
          </button>
        </div>
        <div className="mt-4 text-[13px] text-text-3">
          {sisa !== null ? `Lanjut otomatis dalam ${jam(sisa)}` : 'Tekan Selesai di remote bila lagu sudah habis'}
        </div>
      </div>

      {adaLirik && lirik && (
        <div className="relative z-[1] pt-4 pb-16">
          <LirikKaraoke baris={lirik} waktu={waktuLirik} romanisasi={romFn} ukuran="sedang" />
          <div className="mt-3 text-center text-[11px] text-text-3">
            Waktu lirik diperkirakan sejak Spotify dibuka — geser ±0,5 dtk bila meleset.
          </div>
        </div>
      )}
      <KontrolLirik status={lirik === null ? 'cari' : lirik.length ? 'ada' : 'tidak'} setel={setel} ubah={ubahSetel} />
      {intro && <KartuMasuk judul={baris.judul} artis={baris.artis} penyanyi={baris.diminta_oleh}
        sumber="spotify" selesai={() => setIntro(false)} />}
      <BerikutnyaPojok baris={berikut[0]} tampil={!intro && sisa !== null && sisa <= 25} />
    </div>
  )
}

const Tanda = ({ aktif, warna = 'gold', children }: {
  aktif: boolean; warna?: 'gold' | 'merah'; children: ReactNode
}) => (
  <span className={`px-2.5 py-1 rounded-lg border flex items-center gap-1 whitespace-nowrap ${
    !aktif ? 'border-border text-text-3'
      : warna === 'merah' ? 'border-red-500/40 text-red-400 bg-red-500/10'
      : 'border-gold/40 text-gold bg-gold/10'}`}>{children}</span>
)

// ── 5 Okt 2026: SETELAN & KONTROL LIRIK (YouTube / Spotify) ────────────────
//
// Kontrol lokal di TV: geser ±0,5 dtk (tombol atau [ / ], Shift = ±2 dtk)
// dan sembunyikan/tampilkan (tombol atau L). Disimpan di TV per video/trek.
// Remote BELUM bisa mengaturnya — perlu kolom antrean baru (lihat lib/lirik.ts).

/** Jeda kira-kira antara membuka URI Spotify dan lagunya benar-benar bunyi. */
const JEDA_BUKA_SPOTIFY = 1.5

const geserOffset = (o: number, d: number) => Math.max(-30, Math.min(30, Math.round((o + d) * 10) / 10))

function useSetelanLirik(kunci: string) {
  const [setel, setSetel] = useState<SetelanLirik>(() => bacaSetelanLirik(kunci))
  const offsetRef = useRef(setel.offset)
  offsetRef.current = setel.offset
  const ubahSetel = useCallback((f: (s: SetelanLirik) => SetelanLirik) => {
    setSetel(s => {
      const b = f(s)
      simpanSetelanLirik(kunci, b)
      return b
    })
  }, [kunci])
  useEffect(() => {
    const tekan = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null
      if (el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return
      if (e.key === '[') ubahSetel(s => ({ ...s, offset: geserOffset(s.offset, -0.5) }))
      else if (e.key === ']') ubahSetel(s => ({ ...s, offset: geserOffset(s.offset, 0.5) }))
      else if (e.key === '{') ubahSetel(s => ({ ...s, offset: geserOffset(s.offset, -2) }))
      else if (e.key === '}') ubahSetel(s => ({ ...s, offset: geserOffset(s.offset, 2) }))
      else if (e.key === 'l' || e.key === 'L') ubahSetel(s => ({ ...s, sembunyi: !s.sembunyi }))
    }
    window.addEventListener('keydown', tekan)
    return () => window.removeEventListener('keydown', tekan)
  }, [ubahSetel])
  return { setel, ubahSetel, offsetRef }
}

function KontrolLirik({ keterangan = '' }: {
  status: 'cari' | 'ada' | 'tidak' | 'beda'
  setel: SetelanLirik
  ubah: (f: (s: SetelanLirik) => SetelanLirik) => void
  /** 21 Okt 2026: geseran dari tablet remote (per video, server). */
  geserServer?: number
  keterangan?: string
}) {
  // 27 Okt (Zaldy): "tampilan di layar TV jangan ada Lirik pas, Sembunyikan
  // lirik, sama Vol" — TV tamu bersih: tidak ada tombol −0,5/+0,5, label
  // "Lirik pas", maupun "Sembunyikan lirik". Geser & sembunyikan lirik tetap
  // dari tablet remote (dan tombol [ ] { } L di keyboard PC TV).
  return <span hidden title={keterangan} />
}

// ── LIRIK UNTUK PENILAI SKOR ───────────────────────────────────────────────
// Tampilan lirik pindah ke pemutar/LirikKaraoke.tsx (5 Okt 2026: sapuan biru
// gaya karaoke, menggantikan tampilan 3 baris emas). Dua fungsi di bawah tetap
// dipakai Penilai untuk tahu kapan bagian nyanyi berjalan — logika skor tidak
// berubah.

function indeksLirik(berkas: BerkasLagu | null, t: number): number {
  if (!berkas || !berkas.tersinkron) return -1
  const l = berkas.lirik
  let i = -1
  for (let k = 0; k < l.length; k++) { if (l[k].t <= t) i = k; else break }
  return i
}

function adaLirikAktif(berkas: BerkasLagu | null, t: number): boolean {
  // Tanpa lirik bersinkron, TIDAK ada cara tahu kapan bagian nyanyi berjalan.
  // Dianggap jalan sepanjang lagu — mengurangi ketelitian, tapi tidak
  // menghukum tamu untuk sesuatu yang tidak ia sebabkan.
  if (!berkas || !berkas.tersinkron || berkas.lirik.length === 0) return true
  const i = indeksLirik(berkas, t)
  if (i < 0) return false
  const l = berkas.lirik
  const habis = i + 1 < l.length ? l[i + 1].t : l[i].t + 6
  return t < habis
}

// ── LAYAR SKOR ─────────────────────────────────────────────────────────────

function LayarSkor({ hasil, baris, berikut, lanjut }: {
  hasil: NonNullable<Hasil>; baris: BarisAntrean
  berikut: BarisAntrean[]; lanjut: () => void
}) {
  useEffect(() => {
    // 12 detik lalu lanjut sendiri. Tanpa ini, layar skor menetap sampai ada
    // yang menekannya — dan yang paling sering terjadi adalah room diam
    // memandangi skor sambil menunggu lagu berikutnya yang tidak datang.
    const t = setTimeout(lanjut, 12000)
    return () => clearTimeout(t)
  }, [lanjut])

  const takAdaSuara = hasil.detikBersuara < 5
  const sebutan =
    takAdaSuara ? 'Mikrofon tidak terdengar'
      : hasil.skor >= 90 ? 'Luar biasa'
      : hasil.skor >= 75 ? 'Bagus'
      : hasil.skor >= 55 ? 'Lumayan'
      : 'Coba lagi'
  // 5 Okt 2026: skor tinggi dirayakan (confetti CSS murni).
  const meriah = !takAdaSuara && hasil.skor >= 80

  return (
    <div className="min-h-screen relative overflow-hidden grid place-items-center px-10 text-center">
      <LatarHidup partikel={!meriah} />
      {meriah && <Konfeti />}
      <div className="relative z-[6]">
        <div className="text-[13px] text-text-3 uppercase tracking-[0.25em] tv-naik">
          {baris.judul} — {baris.artis}
        </div>
        {baris.diminta_oleh && (
          <div className="mt-1 text-[16px] text-text-2 tv-naik" style={{ animationDelay: '100ms' }}>
            <Mic size={14} className="inline -mt-0.5 mr-1 text-gold-1" />{baris.diminta_oleh}
          </div>
        )}

        {takAdaSuara ? (
          // Nol karena mikrofonnya tidak masuk BUKAN nol karena nyanyinya
          // jelek. Menampilkan angka besar "0" untuk keduanya menuduh orang
          // atas kerusakan alat.
          <>
            <div className="mt-6 text-5xl font-semibold text-amber-400">{sebutan}</div>
            <p className="mt-4 text-[15px] text-text-3 max-w-md mx-auto leading-relaxed">
              Tidak ada suara yang masuk ke pemutar selama lagu ini, jadi tidak ada
              yang bisa dinilai. Periksa apakah mikrofon room sudah tersambung ke
              perangkat pemutar — skor tidak disimpan.
            </p>
          </>
        ) : (
          <>
            <div className="relative mt-4">
              <div className="tv-skor-cincin" />
              <AngkaNaik nilai={hasil.skor} className="relative tv-skor-angka" />
            </div>
            <div className="text-3xl font-semibold tv-naik" style={{ animationDelay: '1.3s' }}>{sebutan}</div>

            {/* ★ Kalimat ini tidak boleh dihapus.
                Skor mode 'irama' TIDAK menilai ketepatan nada, dan menyebutnya
                begitu berarti memberi tamu angka yang ia percaya padahal tidak
                mengukur apa yang ia kira. */}
            <div className="mt-3 text-[13px] text-text-3">
              {hasil.mode === 'nada'
                ? 'Dinilai dari ketepatan nada terhadap melodi acuan lagu ini.'
                : 'Dinilai dari ketepatan irama dan kestabilan suara — lagu ini belum punya nada acuan, jadi ketepatan nadanya tidak dinilai.'}
            </div>

            <div className="mt-7 flex justify-center gap-10 text-[13px]">
              {hasil.mode === 'nada' && (
                <Butir label="Tepat nada" nilai={hasil.rincian.tepat} />
              )}
              <Butir label="Stabil" nilai={hasil.rincian.stabil} />
              <Butir label="Ikut irama" nilai={hasil.rincian.cakupan} />
            </div>
          </>
        )}

        <button onClick={lanjut}
          className="mt-10 px-8 py-3 rounded-2xl tv-kaca-emas text-[14px]">
          {berikut.length > 0 ? `Lanjut — ${berikut[0].judul}` : 'Selesai'}
        </button>
      </div>
    </div>
  )
}

const Butir = ({ label, nilai }: { label: string; nilai: number }) => (
  <div className="w-32">
    <div className="text-text-3 text-[11.5px]">{label}</div>
    <div className="text-[22px] font-semibold tabular-nums">{Math.round(nilai * 100)}%</div>
    <div className="tv-bar mt-1.5">
      <i style={{ width: `${Math.round(Math.max(0, Math.min(1, nilai)) * 100)}%` }} />
    </div>
  </div>
)

// ── SETELAN ALAMAT MEDIA (sekali per perangkat) ────────────────────────────

function SetelMedia({ selesai }: { selesai: () => void }) {
  const [v, setV] = useState('')
  const [uji, setUji] = useState('')

  function simpan() {
    const b = v.trim().replace(/\/+$/, '')
    if (!b) { setUji('Alamatnya belum diisi'); return }
    if (!/^https?:\/\//.test(b)) {
      // Alamat tanpa skema akan diperlakukan sebagai path relatif dan jatuh ke
      // VM pusat — kegagalan yang persis ingin dihindari layar ini.
      setUji('Harus lengkap dengan http:// atau https://, mis. http://192.168.1.10:8080')
      return
    }
    simpanMedia(b)
    selesai()
  }

  return (
    <div className="min-h-screen grid place-items-center px-8">
      <div className="w-full max-w-lg">
        <h1 className="text-2xl font-semibold">Alamat server lagu</h1>
        <p className="mt-3 text-[13.5px] text-text-3 leading-relaxed">
          Berkas lagu disajikan server di outlet ini lewat jaringan lokal —
          tidak lewat internet. 20.000 lagu itu ratusan gigabyte, dan menariknya
          dari pusat berarti karaoke berhenti setiap kali internet outlet putus.
        </p>
        <p className="mt-2 text-[13.5px] text-text-3 leading-relaxed">
          Isi sekali di perangkat ini. Tersimpan di sini, tidak perlu diisi lagi.
        </p>

        <input
          value={v}
          onChange={e => setV(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && simpan()}
          placeholder="http://192.168.1.10:8080"
          autoCorrect="off" spellCheck={false}
          className="mt-6 w-full bg-bg-1 border border-border rounded-2xl px-4 py-3.5
                     text-[15px] outline-none focus:border-gold/50"
        />
        {uji && <div className="mt-2 text-[12.5px] text-red-400">{uji}</div>}

        <button onClick={simpan}
          className="mt-4 w-full py-3.5 rounded-2xl bg-gold text-black font-semibold">
          Simpan
        </button>

        {/* ── JALAN KELUAR SAAT SERVERNYA MEMANG BELUM ADA ─────────────────
            Layar ini menuntut alamat yang, di outlet yang server lagunya belum
            dipasang, TIDAK ADA jawabannya. Tanpa tombol ini orang terjebak:
            tidak bisa mengisi, tidak bisa lewat, dan tidak bisa melihat apa
            pun dari pemutarnya.

            Yang dilewati cuma pengisian alamatnya. Lagu tetap tidak bisa
            diputar — itu memang tidak mungkin tanpa berkasnya. Bedanya, sisa
            layar bisa dibuka dan diperiksa, dan orangnya tahu KENAPA. */}
        <button
          onClick={() => { lewatiMedia(); selesai() }}
          className="mt-3 w-full py-3 rounded-2xl border border-border text-text-3 text-[13.5px]">
          Belum ada server lagunya — lewati dulu
        </button>
        <p className="mt-2 text-[11.5px] text-text-3 leading-relaxed">
          Melewati ini boleh untuk mencoba tampilan dan alur antrean. Lagu belum
          akan berbunyi sampai alamat servernya diisi — isi nanti lewat tombol
          pengaturan di pemutar.
        </p>

        <p className="mt-5 text-[11.5px] text-text-3 leading-relaxed">
          Tidak tahu alamatnya? Tanyakan ke yang memasang server lagu di outlet
          ini. Ini alamat server media outlet, bukan alamat karaoke.elgroupapp.com —
          yang terakhir tidak menyajikan berkas lagu sama sekali.
        </p>
      </div>
    </div>
  )
}
